"use client";

import { useCallback, useState } from "react";
import type { FormField } from "@/lib/database/forms";
import { getConfirmationTemplate, type FormEmailTemplates } from "@/lib/forms/email-templates";
import { extractSubmittedEmail } from "@/lib/forms/submitted-email";
import { getFormCached, invalidateFormCache } from "@/features/landing-page-v2/_logic/form-prefetch";
import type { SubmittedFormResponse } from "@/features/forms/_logic/viewer-form";

/** The saved response's id + resolved email + the cleaned data that was actually submitted. */
export interface SubmitFormResult {
  responseId: number | null;
  email: string | null;
  cleanedFormData: Record<string, unknown>;
}

interface UseFormSubmitFlowParams {
  formId: number;
  eventId: number;
  landingPageId: number | null;
  respondentTypeId: number | null;
  formData: Record<string, unknown>;
  formFields: FormField[];
  visibleFields: FormField[];
  emailTemplates: FormEmailTemplates | null;
  /** Email fallback sources for the 409-recovery path, when the refetched recap has no answer. */
  viewerEmail: string | null;
  submissionRecapEmail: string | null;
  isEditingSubmission: boolean;
  clearDraft: () => Promise<void>;
  sendConfirmationEmail: (data: Record<string, unknown>, responseId: number | null) => void;
  markSubmitted: () => void;
  showRecap: (recap: SubmittedFormResponse) => void;
  showToast: (message: string, type: "success" | "error") => void;
  /** Called when the form has an autofill template and a filled file is being generated — hands off to useFilledFilePoll. */
  onFilledFile: (target: { responseId: number; token: string; expiry: number }) => void;
}

interface UseFormSubmitFlowReturn {
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
  /**
   * Raw POST to `/api/forms/save-response`. Throws on failure (the error carries
   * a `.status`, so callers can distinguish a 409 single-submission conflict
   * from a real failure without string-matching the message).
   */
  submitForm: (options?: { sendConfirmation?: boolean }) => Promise<SubmitFormResult>;
  /**
   * Orchestrates a non-payment submit: calls `submitForm`, then handles a fresh
   * submission (→ thank-you) vs an edit-save (→ updated recap) on success, and
   * recovers from a 409 by re-fetching the real stored response rather than
   * ever fabricating a recap from the just-rejected local data.
   */
  submit: () => Promise<void>;
}

/**
 * Owns form submission: the raw save-response POST, and the non-payment
 * submit/edit-save/409-recovery orchestration. The payment branch (which needs
 * the raw {@link submitForm} wired through `useFormPayment`'s own
 * `preparePayment`) stays in the caller, since it's already coupled to that
 * hook's return values.
 *
 * @param params - See {@link UseFormSubmitFlowParams}.
 * @returns Submission state and actions (see {@link UseFormSubmitFlowReturn}).
 */
export function useFormSubmitFlow({
  formId,
  eventId,
  landingPageId,
  respondentTypeId,
  formData,
  formFields,
  visibleFields,
  emailTemplates,
  viewerEmail,
  submissionRecapEmail,
  isEditingSubmission,
  clearDraft,
  sendConfirmationEmail,
  markSubmitted,
  showRecap,
  showToast,
  onFilledFile,
}: UseFormSubmitFlowParams): UseFormSubmitFlowReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitForm = useCallback(
    async (options?: { sendConfirmation?: boolean }): Promise<SubmitFormResult> => {
      // Clean skipped fields
      const visibleLabels = new Set(visibleFields.map((f) => f.label));
      const cleanedFormData = { ...formData };
      formFields.forEach((field) => {
        if (!visibleLabels.has(field.label)) {
          cleanedFormData[field.label] = null;
        }
      });

      const response = await fetch("/api/forms/save-response", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // landingPageId lets the server honor the page's inherited SSO grant for a
        // form that has no sso_domain of its own.
        body: JSON.stringify({
          formId,
          userId: null,
          formData: cleanedFormData,
          eventId,
          landingPageId: landingPageId || null,
          respondentTypeId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Failed to save form response" }));
        // Status is attached to the error (not just the message) so callers can
        // distinguish "you already submitted this" (409, single-submission
        // conflict) from a real failure without string-matching the message.
        const submitError = new Error(errorData.error || "Failed to save form response") as Error & {
          status?: number;
        };
        submitError.status = response.status;
        throw submitError;
      }

      const payload = (await response.json()) as {
        success?: boolean;
        responseId?: number;
        filledFile?: { downloadToken: string; downloadExpiry: number } | null;
      };
      if (!payload.success) throw new Error("Failed to submit form");

      // Kick off polling for the filled PDF when this form has autofill configured.
      if (payload.responseId && payload.filledFile) {
        onFilledFile({
          responseId: payload.responseId,
          token: payload.filledFile.downloadToken,
          expiry: payload.filledFile.downloadExpiry,
        });
      }

      // Send confirmation email (non-blocking) — sendFormConfirmationEmail never
      // throws, so this fires without holding up the caller. Skipped when the caller
      // will send it later (payment-enabled forms, once payment is confirmed).
      if (options?.sendConfirmation ?? true) {
        sendConfirmationEmail(cleanedFormData, payload.responseId ?? null);
      }

      const sendToField = getConfirmationTemplate(emailTemplates).sendToField;
      const submittedEmail = extractSubmittedEmail(cleanedFormData, formFields, sendToField);
      return { responseId: payload.responseId ?? null, email: submittedEmail, cleanedFormData };
    },
    [
      visibleFields,
      formData,
      formFields,
      formId,
      eventId,
      landingPageId,
      respondentTypeId,
      emailTemplates,
      onFilledFile,
      sendConfirmationEmail,
    ]
  );

  const submit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const { responseId, email: submittedEmail, cleanedFormData } = await submitForm();
      await clearDraft();
      // A reopened modal must see the fresh submittedResponse, not the
      // pre-submission value memoized when the modal first loaded.
      invalidateFormCache(formId, landingPageId || null);
      if (isEditingSubmission) {
        // The just-saved data is already in hand — no need to re-fetch it.
        showRecap({
          email: submittedEmail ?? viewerEmail ?? submissionRecapEmail ?? null,
          submittedAt: new Date().toISOString(),
          answers: cleanedFormData,
          responseId,
        });
        showToast("Changes saved", "success");
      } else {
        markSubmitted();
      }
    } catch (err) {
      console.error("useFormSubmitFlow: error submitting form", err);
      const status = err instanceof Error && "status" in err && typeof err.status === "number" ? err.status : undefined;
      if (status === 409) {
        // Single-submission conflict. This can mean the visitor's OWN prior
        // submission was detected late (a rare timing gap), or that someone else
        // already submitted under the typed email — never assume the former and
        // fabricate a recap from the just-rejected local data. Only show the
        // recap, and only clear the draft, once a real stored response is
        // confirmed to exist. The cache still holds the pre-submission (no-recap)
        // result from this modal's initial load, so it must be invalidated first.
        invalidateFormCache(formId, landingPageId || null);
        const refreshed = await getFormCached(formId, landingPageId || null);
        if (refreshed.status === "ok" && refreshed.submittedResponse) {
          await clearDraft();
          showRecap(refreshed.submittedResponse);
        } else if (isEditingSubmission) {
          showToast("Your changes weren't saved — this form has already been submitted under that email.", "error");
        } else {
          showToast("You've already submitted this form.", "error");
        }
      } else {
        showToast("Failed to submit form. Please try again.", "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    submitForm,
    clearDraft,
    formId,
    landingPageId,
    isEditingSubmission,
    viewerEmail,
    submissionRecapEmail,
    showRecap,
    showToast,
    markSubmitted,
  ]);

  return { isSubmitting, setIsSubmitting, submitForm, submit };
}
